import React, { useContext, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { useRazorpay } from '../../hooks/useRazorpay';
import cssClass from './PaymentScreen.module.css';
import Header from '../Header';
import Footer from '../Footer';
import Loader from '../Loader';
import back from '../../assets/payment-screen/back.svg';
import tic from '../../assets/payment-screen/yellowTic.svg';
import greenTic from '../../assets/payment-screen/greenTic.svg';
import ecL from '../../assets/payment-screen/Ellipse-L.svg';
import ecR from '../../assets/payment-screen/Ellipse-R.svg';
import SETULoogoSmall from '../../assets/setu-logo-small.svg';
import { useNotification } from '../../hooks/useNotification';

/**
 * @component PaymentScreen
 * @description
 * Handles the end-to-end flow of a payment transaction using Razorpay.
 *
 * This component is responsible for:
 * - Initializing checkout data from route state (`location.state`)
 * - Creating a Razorpay order (`createRazorpayOrder`)
 * - Launching the Razorpay checkout UI (`initiateRazorpayCheckout`)
 * - Verifying the completed payment with the server (`verifyPayment`)
 * - Logging failed payment attempts (`storeFailedPayment`)
 *
 * Key React hooks and side effects used:
 * - `useLayoutEffect`: Redirects if payment page is accessed without context
 * - `useState`: Manages UI states like loading, success, and Razorpay order info
 *
 * Dependencies:
 * - `userDetails`: Authenticated user context, used for prefill and request personalization
 * - `checkoutData`: Populated from routing state, contains pricing and roadmap metadata
 * - `navigate`, `location`: Used for controlled routing post-payment or fallback
 *
 * External Services:
 * - Razorpay (via SDK, loaded dynamically) @see https://razorpay.com/docs/payments/payment-gateway/web-integration/custom/build-integration
 * - Backend server (`/p/create-order`, `/p/verify-payment`, `/p/payment-failure`)
 *
 * Security Considerations:
 * - Email is sent in query params and body payloads; ensure HTTPS and backend validation.
 *
 * UX Behavior:
 * - Displays loading states during order creation, verification, and content fetch.
 * - Redirects user on successful payment.
 * - Shows notification on failure.
 *
 * @returns {JSX.Element} - Payment page UI integrated with Razorpay.
 */
const PaymentScreen = () => {
  // User details from the global context.
  const { userDetails } = useContext(UserContext);
  // Function from custom hook for razorpay
  const { getRazorpayInstance, loadScript, isScriptLoaded } = useRazorpay();
  // Fucntion to show notifications
  const { showNotification } = useNotification();
  // State to store the initial data of the payment we are making, e.g. roadmap_id, price, roadmap name
  const [checkoutData, setCheckoutData] = useState(null);
  // State to check payment is in process. Starts when we create a order id
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  // State to check we are verifying a payment made by user
  const [isPaymentVerifying, setIsPaymentVerifying] = useState(false);
  // State to check payment is successful or not
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  /**
   * Hook to get route details and perform side effects when it changes
   * @see https://api.reactrouter.com/v7/functions/react_router.useLocation.html
   */
  const location = useLocation();
  /**
   * Function to navigate between routes
   * @see https://api.reactrouter.com/v7/functions/react_router.useNavigate.html
   */
  const navigate = useNavigate();

  /**
   * @function createRazorpayOrder
   * @async
   * @description
   * Handles the complete order creation and payment initialization process using Razorpay.
   *
   * This function:
   * 1. Validates the presence of `checkoutData`.
   * 2. Constructs a secure payload for the order request including email, roadmap ID, base price, and GST.
   * 3. Sends a `POST` request to the backend to initiate a Razorpay order.
   * 4. On a successful response (HTTP 200), it:
   *    - Stores the Razorpay order details in local state.
   *    - Loads the Razorpay SDK dynamically.
   *    - Triggers the Razorpay checkout flow.
   * 5. Handles all errors gracefully and ensures `setIsPaymentProcessing` is reset regardless of outcome.
   *
   * This function is side-effect heavy and should be used in a controlled user-triggered context (e.g., button click).
   *
   * @returns {Promise<void>} - Resolves after payment initiation; does not return a value.
   *
   * @see loadScript - Utility function to dynamically load Razorpay SDK.
   * @see initiateRazorpayCheckout - Triggers Razorpay checkout flow with the created order.
   * @see setRazorpayOrderDetails - React state setter to store Razorpay order info.
   * @see checkoutData - Required object containing pricing and roadmap metadata.
   * @see userDetails - User context object containing user identity, specifically `email`.
   * @throws Will log any exceptions encountered during fetch or SDK initialization to the console.
   */
  const createRazorpayOrder = async () => {
    try {
      if (!checkoutData) return;
      const data = {
        email: userDetails?.email,
        r_id: checkoutData.roadmap_id,
        actual_amount: checkoutData.price,
        calculated_gst_amount: (checkoutData.price * 0.18).toFixed(1),
      };
      setIsPaymentProcessing(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/p/create-order`,
        {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        await loadScript();
        initiateRazorpayCheckout(responseData.razorpay_order);
      } else {
        showNotification('error', 'Something went wrong. Please try again');
      }
    } catch (error) {
      console.log(error);
      showNotification('error', 'Something went wrong. Please try again');
    } finally {
      setIsPaymentProcessing(false);
    }
  };

  /**
   * Initializes the Razorpay checkout flow using the provided backend-generated order details.
   *
   * @async
   * @function initiateRazorpayCheckout
   * @param {Object} order_details - Razorpay order object.
   * @param {string} order_details.id - Unique Razorpay order ID.
   * @param {number} order_details.amount - Payment amount in paise.
   *
   * @description
   * Constructs Razorpay options with API key, branding, order metadata, and user prefill.
   * Initializes Razorpay instance, binds failure listener, and opens the modal.
   * Assumes Razorpay SDK is already loaded and `userDetails` is in scope.
   *
   * @see getRazorpayInstance - Returns a configured Razorpay instance.
   * @see verifyPayment - Callback for successful payments.
   * @see storeFailedPayment - Handles failed payment attempts.
   *
   * @returns {Promise<void>}
   */
  const initiateRazorpayCheckout = async (order_details) => {
    try {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_API_KEY,
        amount: order_details.amount,
        currency: 'INR',
        name: 'SETU School',
        image: SETULoogoSmall,
        description: 'Purchase a roadmap',
        order_id: order_details.id,
        handler: (response) => verifyPayment(response),
        prefill: {
          name: userDetails?.name,
          email: userDetails?.email,
          contact: userDetails?.phone,
        },
        theme: { color: '#f8d408' },
        retry: { enabled: false },
      };

      const razorpayInstance = getRazorpayInstance(options);

      razorpayInstance.on('payment.failed', (response) =>
        storeFailedPayment(response),
      );

      razorpayInstance.open();
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Verifies Razorpay payment on the server and handles post-verification UI state.
   *
   * @async
   * @function verifyPayment
   * @param {Object} payment_data - Payment payload from Razorpay handler.
   *
   * @description
   * Sends a POST request to backend to validate transaction authenticity.
   * If verified, sets success state and redirects user to post-payment destination.
   * Skips verification if payment contains errors or `checkoutData` is missing.
   *
   * @returns {Promise<void>}
   *
   * @see setIsPaymentVerifying - Toggles verification loading UI.
   * @see setIsPaymentSuccess - Flags successful payment.
   * @see checkoutData.redirectTo - Post-payment redirect path.
   */
  const verifyPayment = async (payment_data) => {
    try {
      if (!checkoutData || payment_data?.error) return;
      const data = {
        ...payment_data,
      };
      setIsPaymentVerifying(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/p/verify-payment`,
        {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        setIsPaymentSuccess(true);
        setIsPaymentVerifying(false);
        setTimeout(() => {
          // reright the history so that back from roadmap page go to landing page byb skipping payment page
          navigate('/', { replace: true });
          navigate(checkoutData.redirectTo, {
            state: { paymentSuccess: true },
          });
        }, 2000);
      } else {
        showNotification(
          'error',
          'Payment verification failed. Please try again',
        );
      }
    } catch (error) {
      showNotification(
        'error',
        'Payment verification failed. Please try again',
      );
    } finally {
      setIsPaymentVerifying(false);
    }
  };

  /**
   * Persists failed Razorpay payment details to the backend for logging or auditing.
   *
   * @async
   * @function storeFailedPayment
   * @param {Object} payment_data - Razorpay failure payload received via `payment.failed` event.
   *
   * @description
   * Extracts metadata from Razorpay's failure response and posts it to the server.
   * Triggers an error notification for the user. This function is non-blocking to the UI flow.
   * Fields logged: `payment_id`, `order_id`, `code`, `description`, `source`, and `reason`.
   *
   * @returns {Promise<void>}
   *
   * @see showNotification - Displays a toast indicating payment failure.
   */
  const storeFailedPayment = async (payment_data) => {
    try {
      showNotification('error', 'Payment failed!! Please try again');
      const data = {
        razorpay_payment_id: payment_data?.error?.metadata?.payment_id,
        razorpay_order_id: payment_data?.error?.metadata?.order_id,
        error_code: payment_data?.error?.code,
        error_description: payment_data?.error?.description,
        error_source: payment_data?.error?.source,
        error_reason: payment_data?.error?.reason,
      };
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/p/payment-failure`,
        {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
      } else {
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // Navigates the user back to the previous page using browser history.
  const handleBack = () => {
    window.history.back();
  };

  /**
   * useLayoutEffect to validate `location.state`.
   * If `price` is undefined (i.e., invalid or direct access), redirect to home.
   * Otherwise, initialize `checkoutData` with routing state.
   */
  useLayoutEffect(() => {
    if (location.state?.price === undefined) {
      window.location.pathname = '/';
    } else {
      setCheckoutData(location.state);
    }
  }, []);

  return (
    <div className={cssClass['container']}>
      <div className={cssClass['container']}>
        <div className={cssClass['main']}>
          <Header />
          <div className={cssClass['main-content']}>
            {isPaymentVerifying ? (
              <div className={cssClass['loading-div']}>
                <Loader
                  height={'50px'}
                  width={'50px'}
                  text={
                    'Verifying your payment. Please do not refresh the page'
                  }
                />
              </div>
            ) : isPaymentSuccess ? (
              <div className={cssClass['loading-div']}>
                <img
                  src={greenTic}
                  alt=""
                  className={cssClass['success-icon']}
                />
                <p className={cssClass['success-text']}>Payment Successful!!</p>
              </div>
            ) : (
              <>
                <div className={cssClass['back-content']} onClick={handleBack}>
                  <img src={back} alt="" />
                  <p className={cssClass['back-text']}>Back</p>
                </div>
                <div className={cssClass['content']}>
                  <div className={cssClass['main-container']}>
                    <div className={cssClass['pricing']}>Pricing</div>
                    <div className={cssClass['headerNext']}>
                      <div className={cssClass['left']}>
                        <div className={cssClass['heading-div']}>
                          <div className={cssClass['heading']}>
                            <div className={cssClass['heading-img-div']}>
                              <img src={ecL} alt="" />
                              <img src={ecR} alt="" />
                            </div>
                            <p>
                              {checkoutData?.roadmap_title || 'Roadmap Name'}
                            </p>
                          </div>
                        </div>

                        <div className={cssClass['amount-div']}>
                          <div className={cssClass['project-amount']}>
                            <p>Roadmap Fee</p>

                            <span className={cssClass['payment-span']}>
                              {(checkoutData?.price || 0).toFixed(1)}
                            </span>
                          </div>
                          <hr className={cssClass['payment-hr']} />
                          <div className={cssClass['project-details']}>
                            <p>18 % GST</p>
                            <span className={cssClass['payment-span']}>
                              {((checkoutData?.price || 0) * 0.18).toFixed(1)}
                            </span>
                          </div>
                          <div className={cssClass['project-details']}>
                            <p>Coupon Code Applied </p>
                            <span className={cssClass['payment-span']}>0</span>
                          </div>

                          <div className={cssClass['project-coupone']}>
                            Promotions
                            <div className={cssClass['inp-div']}>
                              <input
                                type="text"
                                name=""
                                id=""
                                className={cssClass['inp-coupone']}
                                placeholder="Enter Coupon Code"
                              />
                              <button className={cssClass['apply']}>
                                Apply
                              </button>
                            </div>
                          </div>
                          <hr className={cssClass['payment-hr']} />
                          <div className={cssClass['project-details-3']}>
                            <span className={cssClass['label']}>To Pay</span>{' '}
                            <span className={cssClass['payment-span']}>
                              {((checkoutData?.price || 0) * 1.18).toFixed(1)}
                            </span>
                          </div>
                        </div>

                        <button
                          className={cssClass['start-btn']}
                          onClick={createRazorpayOrder}
                          disabled={isPaymentProcessing}
                        >
                          {isPaymentProcessing && (
                            <Loader height={'27px'} width={'27px'} />
                          )}
                          <p>
                            {isPaymentProcessing
                              ? 'Processing . . .'
                              : 'Continue'}
                          </p>
                        </button>
                      </div>
                      <div className={cssClass['right']}>
                        <p className={cssClass['include']}>What’s included</p>
                        <div className={cssClass['include-point-ct']}>
                          <div className={cssClass['include-point']}>
                            <img src={tic} alt="" />
                            <p>Access to all the premium content</p>
                          </div>
                          <div className={cssClass['include-point']}>
                            <img src={tic} alt="" />
                            <p>Able to register to live events from SETU</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default PaymentScreen;
